// ── Constants ─────────────────────────────────────────────────────────────────

export const PAY_SETTINGS_TABS = [
  "customerPaySettingsTab",
  "internalPaySettingsTab",
  "warrantyPaySettingsTab",
] as const;

export type PaySettingsTab = (typeof PAY_SETTINGS_TABS)[number];

export const TAB_LABELS: Record<PaySettingsTab, string> = {
  customerPaySettingsTab: "Customer Pay",
  internalPaySettingsTab: "Internal Pay",
  warrantyPaySettingsTab: "Warranty Pay",
};

export const TAB_SUFFIX: Record<PaySettingsTab, "_cp" | "_ip" | "_wp"> = {
  customerPaySettingsTab: "_cp",
  internalPaySettingsTab: "_ip",
  warrantyPaySettingsTab: "_wp",
};

// ── Pure helpers ──────────────────────────────────────────────────────────────

export const isPaySettingsTab = (key: string): key is PaySettingsTab =>
  PAY_SETTINGS_TABS.includes(key as PaySettingsTab);

export const getNestedFieldKeys = (section: unknown): string[] => {
  if (Array.isArray(section)) return section as string[];
  if (!section || typeof section !== "object") return [];
  return Object.values(section as Record<string, unknown>).flatMap(
    getNestedFieldKeys,
  );
};

export const replaceSuffix = (key: string, from: string, to: string): string =>
  key.endsWith(from) ? `${key.slice(0, -from.length)}${to}` : key;

export const transformValueSuffixes = (
  value: unknown,
  from: string,
  to: string,
): unknown => {
  if (Array.isArray(value))
    return value.map((item) => transformValueSuffixes(item, from, to));
  if (value && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>).map(([k, v]) => [
        replaceSuffix(k, from, to),
        transformValueSuffixes(v, from, to),
      ]),
    );
  }
  return value;
};

export const getTargetTabsLabel = (tabs: string[]): string => {
  const labels = tabs.map((tab) => TAB_LABELS[tab as PaySettingsTab] ?? tab);
  if (labels.length <= 1) return labels[0] ?? "";
  if (labels.length === 2) return `${labels[0]} and ${labels[1]}`;
  return `${labels.slice(0, -1).join(", ")}, and ${labels[labels.length - 1]}`;
};
